import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Heart, Users, Trophy, Lightbulb, Globe, Star, Crown, Target, HelpCircle } from "lucide-react";
import { useSEO } from '@/hooks/useSEO';

const faqItems = [
  {
    category: "AI Challengers",
    questions: [
      {
        q: "What are AI Challengers?",
        a: "AI Challengers are friendly computer players that keep the arena active! They step in when no human players are available, so you never have to wait long for a game."
      },
      {
        q: "Can I tell if I'm playing against an AI?",
        a: "We blend AI Challengers naturally into the player pool to keep things fun and seamless. They have different skill levels and play styles, just like real players!"
      },
      {
        q: "Is playing against AI fair for my rating?",
        a: "Absolutely! AI Challengers are matched to your skill level, so games are always competitive and help you improve. Your rating changes just like in any other game."
      }
    ]
  },
  {
    category: "Matchmaking",
    questions: [
      {
        q: "How does matchmaking work?",
        a: "When you click Play, we search for players with a similar ELO rating. If we find someone quickly, great! If the queue is empty, an AI Challenger joins so you can start playing right away."
      },
      {
        q: "Why do I sometimes wait for a match?",
        a: "We try to find the best possible opponent for you. Sometimes this takes a few seconds. If no one's available, an AI Challenger will join within 30 seconds!"
      },
      {
        q: "What are the different time controls?",
        a: "We offer Bullet (1-2 min), Blitz (3-5 min), Rapid (10-15 min), and Classical (30+ min). Pick whatever feels right for your mood!"
      }
    ]
  },
  {
    category: "Fair Play",
    questions: [
      {
        q: "How do you prevent cheating?",
        a: "We monitor games for suspicious patterns and use various detection methods. Cheating ruins the fun for everyone, so we take it seriously!"
      },
      {
        q: "What happens if someone cheats?",
        a: "Cheaters may have their accounts restricted or banned. We want ChessHatch to be a fair and fun place for everyone."
      },
      {
        q: "How do I report a player?",
        a: "You can report players through their profile page. Our team reviews reports and takes appropriate action to keep the community safe."
      }
    ]
  }
];
const About = () => {
  // SEO Configuration
  useSEO({
    title: "About ChessHatch - Youth-Focused Chess Learning Platform",
    description: "Created by Ved, a passionate young chess player from Australia. ChessHatch makes learning chess fun and accessible for young people worldwide through interactive gameplay and education.",
    keywords: "about chesshatch, chess education, youth chess, australia chess, ved chess, interactive chess learning",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "Organization",
        "name": "ChessHatch",
        "founder": {
          "@type": "Person",
          "name": "Ved",
          "nationality": "Australian",
          "description": "Primary school student and chess enthusiast"
        },
        "description": "Youth-focused chess learning platform created to enrich young lives through chess education",
        "target": "Young chess learners worldwide"
      }
    }
  });

  return (
    <ChessAppLayout>
      <main className="min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <header className="text-center mb-16">
            <Badge className="mb-4 px-4 py-2 bg-gradient-to-r from-teen-purple/20 to-teen-pink/20 text-teen-purple border-teen-purple/30">
              <Crown className="w-4 h-4 mr-2" />
              About ChessHatch
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-teen-purple via-teen-pink to-teen-cyan bg-clip-text text-transparent">
              Our Story
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              ChessHatch was born from a young chess enthusiast's vision to make chess more accessible and enjoyable for everyone.
            </p>
          </header>

        <div className="space-y-6">
          <Card className="bg-gradient-to-br from-teen-gold/5 to-teen-green/5 border-teen-gold/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-teen-gold" />
                Our Story
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p className="text-lg leading-relaxed mb-4">
                ChessHatch is a playful chess playground created by <strong>Ved</strong>, a primary school boy from Australia, 
                who loves the game and wants to enrich the lives of young people everywhere by teaching and playing chess. 
                Ved built ChessHatch so young people can discover chess in a friendly, fun place—whether they're total beginners 
                or chasing their next big win.
              </p>
              <div className="mt-6 p-4 bg-gradient-to-r from-teen-gold/10 to-teen-green/10 rounded-lg border border-teen-gold/20">
                <h4 className="font-semibold text-teen-gold mb-2">🚀 Latest Achievements (2025)</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Advanced AI engine with multiple difficulty levels</li>
                  <li>• Comprehensive security monitoring and data protection</li>
                  <li>• Interactive puzzle training with 1000+ problems</li>
                  <li>• Real-time multiplayer with enhanced matchmaking</li>
                  <li>• Mobile-optimized responsive design</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-teen-blue" />
                Why Chess for Young People?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-start gap-3 p-4 bg-teen-green/5 rounded-lg border border-teen-green/20">
                  <div className="w-2 h-2 bg-teen-green rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-green">Sharper Thinking</h4>
                    <p className="text-sm text-muted-foreground">Chess trains logic, planning, and decision-making.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-teen-blue/5 rounded-lg border border-teen-blue/20">
                  <div className="w-2 h-2 bg-teen-blue rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-blue">Focus & Patience</h4>
                    <p className="text-sm text-muted-foreground">Players learn to slow down, spot patterns, and think ahead.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-teen-purple/5 rounded-lg border border-teen-purple/20">
                  <div className="w-2 h-2 bg-teen-purple rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-purple">Resilience</h4>
                    <p className="text-sm text-muted-foreground">Win or lose, every game teaches reflection and bounce-back.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-teen-gold/5 rounded-lg border border-teen-gold/20">
                  <div className="w-2 h-2 bg-teen-gold rounded-full mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teen-gold">Creativity & Confidence</h4>
                    <p className="text-sm text-muted-foreground">There are many ways to play—find your style and own it.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-teen-green/5 to-teen-purple/5 rounded-lg border border-teen-green/20">
                <Users className="h-5 w-5 text-teen-green mt-1" />
                <div>
                  <h4 className="font-semibold text-teen-green">Community</h4>
                  <p className="text-sm text-muted-foreground">Make friends, play fair, and learn together across the globe.</p>
                </div>
              </div>
            </CardContent>
          </Card>


          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-teen-gold" />
                What Makes ChessHatch Special
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-3">
                <Badge variant="secondary" className="px-3 py-2 bg-teen-green/10 text-teen-green border-teen-green/30 justify-center">
                  Youth-Focused Design
                </Badge>
                <Badge variant="secondary" className="px-3 py-2 bg-teen-blue/10 text-teen-blue border-teen-blue/30 justify-center">
                  Safe Environment
                </Badge>
                <Badge variant="secondary" className="px-3 py-2 bg-teen-purple/10 text-teen-purple border-teen-purple/30 justify-center">
                  Educational Tools
                </Badge>
                <Badge variant="secondary" className="px-3 py-2 bg-teen-gold/10 text-teen-gold border-teen-gold/30 justify-center">
                  Friendly Community
                </Badge>
                <Badge variant="secondary" className="px-3 py-2 bg-teen-green/10 text-teen-green border-teen-green/30 justify-center">
                  Interactive Learning
                </Badge>
                <Badge variant="secondary" className="px-3 py-2 bg-teen-blue/10 text-teen-blue border-teen-blue/30 justify-center">
                  Progress Tracking
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-teen-blue" />
                Join the Journey
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p className="text-lg leading-relaxed mb-4">
                Pick a bot, invite a friend, or jump into a match—then review your moves and hatch new ideas.
              </p>
              <p className="text-xl font-semibold text-teen-gold">
                Play, learn, and grow with ChessHatch! 🚀♟️
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-teen-cyan/5 to-teen-blue/5 border-teen-cyan/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-teen-cyan" />
                AI Challengers & Transparency
              </CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p className="text-muted-foreground leading-relaxed">
                <span className="text-lg font-semibold">🐣 ChessHatch is still hatching!</span> To avoid empty queues 
                and keep things fun, we run multiple AI Challenger accounts. They may use fictional 
                names and avatars, and can appear in matches and on the leaderboard. They're here to 
                keep the arena active — <strong>humans are still the main event!</strong>
              </p>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-teen-blue" />
                Frequently Asked Questions
              </CardTitle>
              <CardDescription>
                Got questions? We've got answers!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((category, catIndex) => (
                  <div key={category.category} className="mb-4">
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                      {category.category === "AI Challengers" && "🤖"}
                      {category.category === "Matchmaking" && "🎯"}
                      {category.category === "Fair Play" && "🛡️"}
                      {category.category}
                    </h3>
                    {category.questions.map((item, qIndex) => (
                      <AccordionItem 
                        key={`${catIndex}-${qIndex}`} 
                        value={`item-${catIndex}-${qIndex}`}
                        className="border-b border-border/50"
                      >
                        <AccordionTrigger className="text-left hover:text-teen-cyan transition-colors">
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </div>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </div>
      </main>
    </ChessAppLayout>
  );
};

export default About;